import type { HTMLAttributes } from 'react';
import { ProgressBar } from '../../basic/ProgressBar';
import styles from './ProgressInfo.module.css';

export interface ProgressInfoProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /**
   * Label for the spent amount
   * @default "GASTADO:"
   */
  spentLabel?: string;
  /**
   * Explicit spent value to display. If not provided, it will be calculated from 'spent' prop.
   */
  spentValue?: string | number;
  /**
   * Label for the available amount
   * @default "DISPONIBLE:"
   */
  availableLabel?: string;
  /**
   * Explicit available value to display. If not provided, it will be calculated from 'total' - 'spent'.
   */
  availableValue?: string | number;
  /**
   * Total amount (max value for progress)
   */
  total: number;
  /**
   * Current spent amount
   */
  spent: number;
  /**
   * Color for the progress bar and spent pip
   * @default 'info'
   */
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info';
  /**
   * Size of the progress bar
   * @default 'md'
   */
  progressSize?: 'sm' | 'md' | 'lg';
  /**
   * Function to format numeric values
   */
  formatValue?: (value: number) => string;
}

export function ProgressInfo({
  spentLabel = 'GASTADO:',
  spentValue,
  availableLabel = 'DISPONIBLE:',
  availableValue,
  total,
  spent,
  color = 'info',
  progressSize = 'md',
  formatValue,
  className,
  ...props
}: ProgressInfoProps) {
  // Calculate values
  const available = Math.max(0, total - spent);
  
  // Calculate percentage for progress bar
  const percentage = total > 0 ? (spent / total) * 100 : 0;

  // Determine display values
  const displaySpent = spentValue ?? (formatValue ? formatValue(spent) : spent);
  const displayAvailable = availableValue ?? (formatValue ? formatValue(available) : available);

  const containerClasses = [
    styles.container,
    className
  ].filter(Boolean).join(' ');

  // Map color to light variant for track
  const trackColorMap: Record<string, string> = {
    primary: 'var(--color-brand-light)',
    success: 'var(--color-success-light)',
    warning: 'var(--color-warning-light)',
    error: 'var(--color-error-light)',
    info: 'var(--color-info-light)',
  };

  const trackColor = trackColorMap[color] || trackColorMap.info;

  return (
    <div className={containerClasses} {...props}>
      <div className={styles.labelsRow}>
        <div className={styles.label}>
          <span className={styles.pip} data-color={color} />
          <span className={styles.labelText}>
            {spentLabel} {displaySpent}
          </span>
        </div>
        <div className={styles.label}>
          <span className={styles.pip} data-color={color} data-variant="light" />
          <span className={styles.labelText}>
            {availableLabel} {displayAvailable}
          </span>
        </div>
      </div>
      <div className={styles.progressBarWrapper} style={{ '--track-color': trackColor } as React.CSSProperties}>
        <ProgressBar 
          value={percentage} 
          max={100} 
          size={progressSize} 
          color={color}
        />
      </div>
    </div>
  );
}
